import React, { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Pause, Play } from "lucide-react";

const transcriptId = "interactive-demo-transcript";

export default function InteractiveDemo() {
  const shouldReduceMotion = useReducedMotion();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [replayKey, setReplayKey] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoadVideo(true);
          observer.disconnect();
        }
      },
      { rootMargin: "240px 0px" },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const replay = () => {
    if (videoRef.current && !videoFailed) {
      videoRef.current.currentTime = 0;
      void videoRef.current.play().catch(() => {
        setVideoFailed(true);
      });
      return;
    }

    setReplayKey((value) => value + 1);
  };

  const togglePlayback = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (!videoRef.current || videoFailed) {
      return;
    }

    if (videoRef.current.paused) {
      void videoRef.current.play().catch(() => {
        setVideoFailed(true);
      });
      return;
    }

    videoRef.current.pause();
  };

  return (
    <section id="try-mapleos" className="scroll-mt-24 px-6 py-20 md:py-28">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.55 }}
          className="mx-auto mb-10 max-w-3xl text-center"
        >
          <h2 className="mt-5 text-3xl font-semibold tracking-[-0.05em] text-white md:text-5xl">
            Try MapleOS in 10 seconds.
          </h2>
          <p className="mt-4 text-base leading-7 text-white/60 md:text-lg">
            Click <a href="/start" className="text-primary underline">Launch</a> to try it yourself.
          </p>
        </motion.div>

        <motion.div
          ref={containerRef}
          onClick={replay}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              replay();
            }
          }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.55, delay: 0.1 }}
          className="group relative block w-full overflow-hidden rounded-[32px] border border-white/10 bg-[linear-gradient(180deg,rgba(13,18,30,0.96),rgba(2,6,23,0.98))] text-left shadow-[0_28px_120px_rgba(0,0,0,0.35)]"
          role="button"
          tabIndex={0}
          aria-label="Replay MapleOS recorded demo"
          aria-describedby={transcriptId}
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(132,255,76,0.18),transparent_30%),radial-gradient(circle_at_bottom_left,rgba(56,189,248,0.15),transparent_34%)]" />
          <div className="relative flex items-center justify-between border-b border-white/10 px-5 py-4 text-xs uppercase tracking-[0.28em] text-white/55">
            <div className="flex items-center">
              <span className="h-3 w-3 rounded-full bg-primary/90 shadow-[0_0_10px_hsl(var(--primary)/0.45)]" />
            </div>
            <span>mapleos.desktop.capture</span>
          </div>

          <div className="relative aspect-[16/9] overflow-hidden">
            {!videoFailed && shouldLoadVideo && (
              <video
                ref={videoRef}
                className="absolute inset-0 h-full w-full object-cover"
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                poster=""
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onError={() => setVideoFailed(true)}
              >
                <source src="/videos/mapleos.webm" type="video/webm" />
              </video>
            )}

            <motion.div
              key={replayKey}
              animate={
                shouldReduceMotion
                  ? undefined
                  : {
                      opacity: [0.82, 1, 0.82],
                    }
              }
              transition={{ duration: 3.8, ease: "easeInOut" }}
              className="absolute inset-0"
            ></motion.div>

            <div className="absolute left-4 top-4 rounded-full border border-white/10 bg-black/35 px-3 py-2 text-xs text-white/70 backdrop-blur md:left-6 md:top-6">
              click anywhere to replay
            </div>

            {[
              { label: "AI chat", left: "18%", top: "24%" },
              { label: "Drag window", left: "48%", top: "40%" },
              { label: "Blueprint run", left: "70%", top: "66%" },
            ].map((hotspot) => (
              <div
                key={hotspot.label}
                className="pointer-events-none absolute hidden -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/10 bg-background/70 px-3 py-1 text-[11px] text-white/75 backdrop-blur md:block"
                style={{ left: hotspot.left, top: hotspot.top }}
              >
                {hotspot.label}
              </div>
            ))}

            <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 bg-gradient-to-t from-slate-950 via-slate-950/80 to-transparent px-4 pb-4 pt-16 md:px-6 md:pb-6">
              <div>
                <p className="text-sm font-medium text-white">mapleos is packed with powerful features like our Blueprint Studio</p>
                <p className="mt-1 text-sm text-white/55">We unleash the power of AI in a way that is intuitive and accessible. What big tech doesn't want you to have.</p>
              </div>
              <button
                type="button"
                onClick={togglePlayback}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-primary/20 bg-primary/10 text-primary transition-colors hover:bg-primary/16"
                aria-label={isPlaying ? "Pause MapleOS demo video" : "Play MapleOS demo video"}
              >
                {isPlaying ? <Pause className="h-4 w-4 fill-current" /> : <Play className="h-4 w-4 fill-current" />}
              </button>
            </div>
          </div>
        </motion.div>

        <p id={transcriptId} className="sr-only">
          Demo transcript: MapleOS boots, the AI chat opens, a window is repositioned, the terminal launches a blueprint, and the interface returns to a ready state.
        </p>
      </div>
    </section>
  );
}